import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb, { getComicBreadcrumb } from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SEOSchema from '@/components/feature/SEOSchema';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-foreground-500 text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-50 mb-2">
              漫画未找到
            </h1>
            <p className="text-foreground-500 text-sm mb-6">
              您要查找的漫画可能已被移除或不存在
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const breadcrumbItems = getComicBreadcrumb(comic);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const title = `${comic.title} - 无删减韩漫在线观看 - 韩漫大全`;
  const description = `${comic.title}${comic.description.slice(0, 80)} - 免费韩国漫画大全，无删减韩漫在线阅读`;

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        keywords={`${comic.title},韩漫网站,无删减韩漫在线观看,漫画韩漫网址,免费韩国漫画大全,${comic.tags.join(',')}`}
        canonicalUrl={`https://www.manhuakr.com/comic/${comic.id}`}
        ogType="article"
      />
      <SEOSchema
        type="Article"
        data={{
          headline: comic.title,
          description: comic.description,
          author: { '@type': 'Person', name: comic.author },
          datePublished: comic.chapters[0]?.updatedAt,
          dateModified: comic.updatedAt,
          inLanguage: 'zh-CN',
        }}
      />
      <SEOSchema
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.manhuakr.com/' },
            { '@type': 'ListItem', position: 2, name: breadcrumbItems[0]?.label, item: `https://www.manhuakr.com/category/${comic.category}` },
            { '@type': 'ListItem', position: 3, name: comic.title },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Comic Header */}
            <article className="flex flex-col lg:flex-row gap-8 mb-12">
              <div className="flex-shrink-0 w-full sm:w-64 lg:w-72">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩漫在线观看`}
                    title={`${comic.title} - 无删减韩漫`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-3">
                  {comic.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-foreground-400 text-sm">
                    <i className="ri-user-line mr-1"></i>
                    {comic.author}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-accent-500/20 text-accent-400'
                      : 'bg-secondary-500/20 text-secondary-400'
                  }`}>
                    {comic.status}
                  </span>
                  <span className="flex items-center gap-1 text-accent-500 text-sm">
                    <i className="ri-star-fill"></i>
                    <strong>{comic.rating}</strong>
                  </span>
                  <span className="text-foreground-500 text-sm">
                    <i className="ri-eye-line mr-1"></i>
                    {comic.views > 10000
                      ? `${(comic.views / 10000).toFixed(1)}万次`
                      : `${comic.views}次`}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-400 text-xs border border-background-200/70">
                      {tag}
                    </span>
                  ))}
                </div>

                <p className="text-foreground-400 text-sm leading-relaxed mb-6">
                  {comic.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button className="flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm">
                    <i className="ri-play-circle-line"></i>
                    开始阅读
                  </button>
                  <button className="flex items-center justify-center gap-2 bg-background-100 hover:bg-background-200/70 text-foreground-300 hover:text-foreground-50 px-8 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer text-sm border border-background-200/70">
                    <i className="ri-bookmark-line"></i>
                    加入收藏
                  </button>
                </div>
              </div>
            </article>

            {/* Chapter List */}
            <section className="mb-12">
              <h2 className="font-heading text-xl font-bold text-foreground-50 mb-4">
                章节列表
                <span className="text-foreground-500 text-sm font-normal ml-2">
                  共 {comic.chapters.length} 话
                </span>
              </h2>
              <div className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-background-200/70">
                  {comic.chapters.map((chapter) => (
                    <div
                      key={chapter.id}
                      className="flex items-center justify-between px-4 py-3 bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-foreground-300 text-sm font-medium whitespace-nowrap">
                          {chapter.title}
                        </span>
                      </div>
                      <span className="text-foreground-500 text-xs whitespace-nowrap ml-2">
                        {chapter.updatedAt}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Related Comics */}
            {relatedComics.length > 0 && (
              <section>
                <h2 className="font-heading text-xl font-bold text-foreground-50 mb-4">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop>
                  {relatedComics.map((c) => (
                    <Link
                      key={c.id}
                      to={`/comic/${c.id}`}
                      className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                          src={c.coverImage}
                          alt={c.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        {c.isHot && (
                          <span className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-semibold">
                            HOT
                          </span>
                        )}
                      </div>
                      <div className="p-3">
                        <h3 className="text-foreground-50 font-semibold text-sm truncate group-hover:text-primary-400 transition-colors">
                          {c.title}
                        </h3>
                        <div className="flex items-center gap-1 mt-1">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                          <span className="text-foreground-300 text-xs">{c.rating}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}